import { useState, useEffect } from 'react';
import type { Task, TimerMode } from '../../../hooks/usePomodoro';

interface PomodoroTimerProps {
  timeLeft: number;
  timerMode: 'focus' | 'break';
  isRunning: boolean;
  activeTask: any;
  formatTime: (seconds: number) => string;
  onStart: () => void;
  onPause: () => void;
  onReset: () => void;
  totalPomodoros: number;
}

export default function PomodoroTimer({
  timeLeft,
  timerMode,
  isRunning,
  activeTask,
  formatTime,
  onStart,
  onPause,
  onReset,
  totalPomodoros,
}: PomodoroTimerProps) {
  const progress = timerMode === 'focus' 
    ? ((25 * 60 - timeLeft) / (25 * 60)) * 100
    : ((5 * 60 - timeLeft) / (5 * 60)) * 100;

  const circumference = 2 * Math.PI * 120;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-slate-200/50">
      {/* Active Task Display */}
      <div className="text-center mb-6">
        <p className="text-sm text-slate-500 mb-1">Current Focus</p>
        <h2 className="text-lg font-medium text-slate-800 min-h-[1.5rem]">
          {activeTask ? activeTask.text : 'No task selected'}
        </h2>
      </div>

      {/* Timer Circle */}
      <div className="flex justify-center mb-6">
        <div className="relative">
          <svg className="w-64 h-64 transform -rotate-90" viewBox="0 0 256 256">
            {/* Background circle */}
            <circle
              cx="128"
              cy="128"
              r="120"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-slate-200"
            />
            {/* Progress circle */}
            <circle
              cx="128"
              cy="128"
              r="120"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className={`transition-all duration-1000 ease-linear ${
                timerMode === 'focus' ? 'text-blue-500' : 'text-green-500'
              }`}
              strokeLinecap="round"
            />
          </svg>
          
          {/* Timer Display */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-4xl font-bold text-slate-800 mb-2">
              {formatTime(timeLeft)}
            </div>
            <div className={`text-sm font-medium px-3 py-1 rounded-full ${
              timerMode === 'focus' 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-green-100 text-green-700'
            }`}>
              {timerMode === 'focus' ? 'Focus Mode' : 'Break Mode'}
            </div>
          </div>
        </div>
      </div>

      {/* Session Indicator */}
      <div className="text-center mb-6">
        <p className="text-sm text-slate-500 mb-2">Session Progress</p>
        <div className="flex justify-center gap-2">
          {[1, 2, 3, 4].map((session) => (
            <div
              key={session}
              className={`w-3 h-3 rounded-full ${
                session <= Math.floor(totalPomodoros % 4) + 1
                  ? 'bg-blue-500'
                  : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-1">
          Session {Math.floor(totalPomodoros % 4) + 1} of 4
        </p>
      </div>

      {/* Control Buttons */}
      <div className="flex justify-center gap-3">
        <button
          onClick={isRunning ? onPause : onStart}
          className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${
            isRunning
              ? 'bg-orange-500 hover:bg-orange-600 text-white'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
        >
          <i className={`${isRunning ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
          {isRunning ? 'Pause' : 'Start'}
        </button>
        
        <button
          onClick={onReset}
          className="px-6 py-3 rounded-xl font-medium bg-slate-200 hover:bg-slate-300 text-slate-700 transition-all duration-200 flex items-center gap-2 whitespace-nowrap"
        >
          <i className="ri-restart-line"></i>
          Reset
        </button>
      </div>

      {/* Customization Link */}
      <div className="text-center mt-4">
        <button className="text-sm text-slate-500 hover:text-slate-700 transition-colors">
          <i className="ri-settings-3-line mr-1"></i>
          Customize Intervals
        </button>
      </div>
    </div>
  );
}
